import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { authApi } from '@/api'
import { getAuthError } from '@/lib/auth-error'
import { useUiStore } from '@/store/ui.store'
import logoLight from '@/assets/logo/olimhub-logo.png'
import logoDark from '@/assets/logo/olimhub-logo-dark.png'

function PasswordField({
  label,
  value,
  onChange,
  error,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  error?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
        {label}
      </span>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <span style={{ position: 'absolute', insetInlineStart: 12, color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none', zIndex: 1 }}>
          <Lock size={18} />
        </span>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          style={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            padding: '11px 44px 11px 40px',
            border: `1.5px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            background: 'var(--surface)',
            color: 'var(--text-strong)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            insetInlineEnd: 0,
            width: 44,
            height: '100%',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
          }}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  )
}

export function ResetPassword() {
  const { t, i18n } = useTranslation()
  const { theme } = useUiStore()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const logo = theme === 'dark' ? logoDark : logoLight
  const dir = i18n.dir()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (password !== confirm) {
      setError(t('auth.reset_error_mismatch'))
      return
    }

    setLoading(true)
    try {
      await authApi.resetPassword(token, password)
      setSuccess(true)
    } catch (err) {
      setError(getAuthError(err, t, 'reset'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div dir={dir} style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 20px 32px', overflowY: 'auto' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '28px 20px 24px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Link to="/">
              <img src={logo} alt="OlimHub" style={{ height: 34, width: 'auto', display: 'block' }} />
            </Link>
            <div className="oh-spectrum oh-spectrum--smooth" style={{ width: 52, height: 4 }} />
          </div>

          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <h1 style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: 'var(--text-strong)', letterSpacing: '-0.02em', margin: 0 }}>
              {t('auth.reset_title')}
            </h1>
            {!success && !error && (
              <p style={{ margin: '8px auto 0', maxWidth: '30ch', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-normal)', fontFamily: 'var(--font-sans)' }}>
                {t('auth.reset_sub')}
              </p>
            )}
          </div>

          {!token ? (
            <p style={{ margin: 0, textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--danger)' }}>
              {t('auth.reset_error_expired')}
            </p>
          ) : success ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
              <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)', lineHeight: 'var(--lh-normal)' }}>
                {t('auth.reset_success')}
              </p>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '10px 20px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--brand)',
                  color: 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  textDecoration: 'none',
                }}
              >
                {t('auth.recovery_back')}
              </Link>
            </div>
          ) : (
            <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <PasswordField
                label={t('auth.reset_new_password')}
                value={password}
                onChange={setPassword}
              />
              <PasswordField
                label={t('auth.reset_confirm')}
                value={confirm}
                onChange={setConfirm}
                error={!!error && error === t('auth.reset_error_mismatch')}
              />

              {error && (
                <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--danger)', textAlign: 'center', fontFamily: 'var(--font-sans)' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: '13px 0',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--brand)',
                  color: 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  width: '100%',
                }}
              >
                {loading ? t('common.loading') : t('auth.reset_cta')}
              </button>
            </form>
          )}
        </div>

        <Link
          to="/login"
          style={{
            marginTop: 20,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
          }}
        >
          {t('auth.recovery_back')}
        </Link>
      </div>
    </div>
  )
}
